import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { copyToClipboard } from '../lib/copy-to-clipboard.js';
/** Сколько держать галочку после удачного копирования. */
const COPIED_FEEDBACK_MS = 1500;
/**
 * Значение, которое копируется по клику: вся строка — цель клика, иконка
 * копирования проявляется по наведению и фокусу, после удачной записи в
 * буфер полторы секунды стоит галочка, и тост «Скопировано» — через общий
 * `copyToClipboard` кита (тот же низ, что у `CopyButton`).
 *
 * Это вторая санкционированная форма «скопировать» в ките рядом с
 * `CopyButton`: та — кнопка РЯДОМ со значением (иконка или подпись), эта —
 * само значение как кнопка. Идентификаторы в карточке контакта (WIT user,
 * Stripe) — её место: значение длинное, моноширинное, и отдельная кнопка
 * рядом с ним только съедала бы ширину рейла.
 *
 * Нативная `<button>`, а не `Button` кита: у той высота и отступы кнопки
 * действия, а здесь нужен встроенный в текст элемент с высотой строки.
 * Клавиатура (Enter/Space) и фокус — из семантики кнопки, руками ничего не
 * добавляется.
 */
export const CopyValue = React.forwardRef(({ value, label, children, successMessage = 'Скопировано', errorMessage = 'Не удалось скопировать', className, title, ...rest }, ref) => {
    const [copied, setCopied] = React.useState(false);
    const timer = React.useRef(null);
    React.useEffect(() => () => {
        if (timer.current)
            clearTimeout(timer.current);
    }, []);
    async function handleClick() {
        const ok = await copyToClipboard(value, successMessage, errorMessage);
        if (!ok)
            return;
        setCopied(true);
        if (timer.current)
            clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    }
    return (_jsxs("button", { ref: ref, type: "button", "aria-label": `Скопировать ${label}`, title: title ?? `Скопировать ${label}`, "data-copied": copied ? 'true' : undefined, className: cn('group/copy inline-flex max-w-full cursor-pointer items-center gap-1 rounded-[var(--radius-1)] text-left', '-mx-1 px-1 hover:bg-[var(--color-surface-sunken)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)]', className), onClick: () => void handleClick(), ...rest, children: [_jsx("span", { className: "min-w-0 break-all", children: children ?? value }), copied ? (_jsx(Check, { className: "h-3 w-3 shrink-0 text-[var(--color-state-success-fg)]", "aria-hidden": "true", "data-testid": "copy-value-check" })) : (_jsx(Copy, { className: "h-3 w-3 shrink-0 text-[var(--color-text-muted)] opacity-0 transition-opacity group-hover/copy:opacity-100 group-focus-visible/copy:opacity-100", "aria-hidden": "true", "data-testid": "copy-value-icon" }))] }));
});
CopyValue.displayName = 'CopyValue';
//# sourceMappingURL=copy-value.js.map